import { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import html2canvas from "html2canvas";
import {
  getChartSpots,
  calculateNeighbours,
  getPatternDevChartSpots,
  getTotalChartSpots,
  getGazeChartData,
  getExamAlgorithmTranslation,
  getVisualField,
  calculateDurationForDisplay,
  getTutorialCompletionText,
} from "../../utils/examReportsHelper";
import { getDecimalNum } from "../../utils/numberHelper";
import { ErrorMessageComponent } from "../../components/UI/ErrorMessage";
import TotalGraph from "../Report/VisualFieldReport/EyeReport/TotalGraph";
import NewPatternGraph from "../Report/VisualFieldReport/EyeReport/NewPatternGraph";
import NumericalGraph from "../Report/VisualFieldReport/EyeReport/NumericalGraph";
import DeviationChartLegend from "../Report/VisualFieldReport/EyeReport/DeviationChartLegend";
import { ReportInfoShared } from "../../components/UI/ReportInfoShared";
import useClinic from "../../hooks/useClinic";
import { getLocalDateTimeWithZone } from "../../utils/dateHelper";

const ProgressAnalysisEyeReport = ({
  examReport,
  examData,
  forBackendPdf = false,
}) => {
  const { t } = useTranslation();

  const { clinicTimezone } = useClinic();

  const [imageSrc, setImageSrc] = useState(null);
  const [chartData, setChartData] = useState({
    number: undefined,
    pattern: undefined,
    totalDev: undefined,
    patternDev: undefined,
    gaze: undefined,
  });

  const eyeReport = examReport?.exam?.eye;
  const examStats = examReport?.stats;
  const postProcessing = examReport?.postProcessing;
  const isNotRlFast = examReport?.exam?.algorithm !== "SCREEN_FAST";

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(examReport.timing.start, clinicTimezone)
        : null,
    [examReport?.timing?.start, clinicTimezone]
  );

  const gridSizeData = useMemo(
    () => [
      { xs: 6, sm: 6, md: 4, lg: 4, xl: 4 },
      { xs: 6, sm: 6, md: 4, lg: 4, xl: 4 },
      { xs: 6, sm: 6, md: 4, lg: 4, xl: 4 },
      { xs: 6, sm: 6, md: 4, lg: 4, xl: 4 },
      { xs: 6, sm: 6, md: 4, lg: 4, xl: 4 },
    ],
    []
  );

  const infoData = useMemo(
    () => [
      [
        {
          title: t("progress_analysis_exam_algorithm"),
          body: t(
            getExamAlgorithmTranslation(examData?.reportData?.exam?.algorithm)
          ),
        },
      ],
      [
        {
          title: t("progress_analysis_exam_grid_type"),
          body: getVisualField(examData?.reportData?.exam?.grid),
        },
      ],
      [
        {
          title: t("report_exam_date"),
          body: localDateAndTimeWithZone?.date,
          isBlack: true,
        },
      ],
      [
        {
          title: t("report_exam_time"),
          body: localDateAndTimeWithZone?.time,
          isBlack: true,
        },
      ],
      [
        {
          title: t("report_exam_duration"),
          body: calculateDurationForDisplay(
            examData?.reportData?.timing?.elapsed / 1000
          ),
          isBlack: false,
        },
      ],
      [
        getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ) && {
          title: t("word_tutorial"),
          body: getTutorialCompletionText(
            examReport?.postProcessing?.tutorialCompletionState,
            t
          ),
          isBlack: true,
        },
      ],
    ],
    [
      examData,
      localDateAndTimeWithZone,
      examReport?.postProcessing?.tutorialCompletionState,
      t,
    ]
  );

  const handleShowImage = useCallback(async () => {
    const element = document.getElementById(`eye-report-${examData?.id}`);
    if (!element) {
      console.error("Element not found!");
      return;
    }

    try {
      const canvas = await html2canvas(element, { scale: 2 });
      setImageSrc(canvas.toDataURL("image/jpg"));
    } catch (error) {
      console.error("Error generating image:", error);
    }
  }, [examData?.id]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleShowImage();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [handleShowImage]);

  useEffect(() => {
    if (!examReport) return;

    const spots = getChartSpots(examReport);
    const finalPatternSpots = calculateNeighbours(spots);
    const { gazeSpots } = getGazeChartData(examReport);

    setChartData({
      number: spots,
      pattern: finalPatternSpots,
      totalDev: getTotalChartSpots(examReport),
      patternDev: getPatternDevChartSpots(examReport),
      gaze: gazeSpots,
    });
  }, [examReport]);

  if (!eyeReport || !examData || !examReport || !examData.isRetinaLogikExam)
    return null;

  return (
    <div style={{ width: "100%" }}>
      {postProcessing ? (
        imageSrc ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <img
              className="retinalogik-report"
              src={imageSrc}
              alt="Generated"
              width="100%"
              height="auto"
            />
          </div>
        ) : (
          <Grid
            data-testid={`eye-report-${examData?.id}`}
            id={`eye-report-${examData?.id}`}
            container
            sx={{
              width: "100%",
              whiteSpace: "nowrap",
            }}
          >
            {isNotRlFast && (
              <>
                <Grid
                  container
                  display="flex"
                  border="1px solid black"
                  borderRadius="4px 4px 0px 0px"
                  p={1}
                >
                  <ReportInfoShared
                    data={infoData}
                    gridSizeData={gridSizeData}
                  />
                </Grid>
                <Grid
                  container
                  justifyContent="space-around"
                  rowSpacing={2}
                  border="1px solid black"
                  borderTop="none"
                  borderBottom="none"
                >
                  <Grid item xs>
                    <Paper
                      elevation={0}
                      sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        py: "12px",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "center",
                        }}
                      >
                        {t("Greyscale")}
                      </Typography>
                      <div
                        style={{
                          transform: "scale(0.8)",
                          display: "flex",
                          justifyContent: "center",
                          minHeight: "300px",
                        }}
                      >
                        <NewPatternGraph
                          pressedPoints={chartData.pattern || []}
                          eye={eyeReport}
                          gridType={examReport?.exam?.grid}
                        />
                      </div>
                    </Paper>
                  </Grid>

                  <Grid item xs>
                    <Paper
                      elevation={0}
                      sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        py: "12px",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "center",
                        }}
                      >
                        {t("Total Deviation")}
                      </Typography>
                      <div
                        style={{
                          transform: "scale(0.8)",
                          display: "flex",
                          justifyContent: "center",
                          marginTop: "8px",
                          minHeight: "300px",
                        }}
                      >
                        <NumericalGraph
                          processedPoints={
                            postProcessing?.processedPoints || []
                          }
                          gridType={examReport?.exam?.grid}
                        />
                      </div>
                    </Paper>
                  </Grid>

                  <Grid item xs>
                    <Paper
                      elevation={0}
                      sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        py: "12px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          flexDirection: "column",
                        }}
                      >
                        <div>
                          <Typography
                            variant="body1"
                            sx={{
                              textAlign: "center",
                            }}
                          >
                            {t("Total Deviation Percentile")}
                          </Typography>
                          <div
                            style={{
                              transform: "scale(0.9)",
                              display: "flex",
                              justifyContent: "center",
                              marginTop: "28px",
                              minHeight: "300px",
                            }}
                          >
                            <TotalGraph
                              pressedPoints={chartData.totalDev || []}
                              eye={eyeReport}
                              gridType={examReport?.exam?.grid}
                            />
                          </div>
                        </div>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          <DeviationChartLegend />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
                <Grid
                  container
                  display="flex"
                  border="1px solid black"
                  borderRadius="0px 0px 4px 4px"
                  p={1}
                >
                  <Grid item container xs={12}>
                    <Grid item xs>
                      <Typography
                        data-testid={`mean-deviation-${examData.id}`}
                        variant="body2"
                      >
                        <span style={{ fontWeight: "bold" }}>MD: </span>
                        {postProcessing?.totalMeanDeviation !== null
                          ? `${getDecimalNum(postProcessing?.totalMeanDeviation, 2)} dB`
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs>
                      <Typography
                        data-testid={`pattern-standard-deviation-${examData.id}`}
                        variant="body2"
                      >
                        <span style={{ fontWeight: "bold" }}>PSD: </span>
                        {postProcessing?.patternStandardDeviation !== null
                          ? `${getDecimalNum(postProcessing?.patternStandardDeviation, 2)} dB`
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs>
                      <Typography
                        data-testid={`false-positive-${examData.id}`}
                        variant="body2"
                      >
                        <span style={{ fontWeight: "bold" }}>FP: </span>
                        {examStats?.falsePositivePercentage
                          ? `${getDecimalNum(examStats?.falsePositivePercentage, 2)} %`
                          : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs>
                      <Typography
                        data-testid={`fixation-loss-${examData.id}`}
                        variant="body2"
                      >
                        <span style={{ fontWeight: "bold" }}>FL: </span>
                        {examStats?.fixationLossPercentage !== null
                          ? `${getDecimalNum(examStats?.fixationLossPercentage, 2)} %`
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs>
                      <Typography
                        data-testid={`retinalogik-field-index-${examData.id}`}
                        variant="body2"
                      >
                        <span style={{ fontWeight: "bold" }}>VRFI: </span>
                        {postProcessing?.retinaLogikFieldIndex !== null &&
                        !isNaN(postProcessing?.retinaLogikFieldIndex)
                          ? `${getDecimalNum(postProcessing?.retinaLogikFieldIndex, 2)} %`
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs>
                      <Typography
                        data-testid={`false-negative-${examData.id}`}
                        variant="body2"
                      >
                        <span style={{ fontWeight: "bold" }}>FN: </span>
                        {examStats?.falseNegativeCount !== null
                          ? `${examStats?.falseNegativeCount} %`
                          : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        )
      ) : (
        <ErrorMessageComponent errorBit={1} />
      )}
    </div>
  );
};

export default ProgressAnalysisEyeReport;
